import { useRef, useState } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Tooltip,
} from '@mui/material';

import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export default function ImagePicker({
    value,
    onChange,
    size = 160,
    rounded = true,
    maxSizeMB = 5,
    disabled = false,
}) {
    const inputRef = useRef(null);
    const [isHovering, setIsHovering] = useState(false);
    const [error, setError] = useState('');

    const handleClick = () => {
        if (disabled) return;

        inputRef.current?.click();
    };

    const handleChange = (event) => {
        const file = event.target.files?.[0];

        if (!file) return;

        setError('');

        // Validação do tipo
        if (!file.type.startsWith('image/')) {
            setError('Selecione uma imagem válida.');
            return;
        }

        // Validação do tamanho
        if (file.size > maxSizeMB * 1024 * 1024) {
            setError(`A imagem deve ter no máximo ${maxSizeMB}MB.`);
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            onChange?.(reader.result);
        };

        reader.onerror = () => {
            setError('Não foi possível carregar a imagem.');
        };

        reader.readAsDataURL(file);

        // Permite selecionar novamente o mesmo arquivo
        event.target.value = '';
    };

    const handleRemove = (event) => {
        event.stopPropagation();

        onChange?.(null);
    };

    return (
        <Box
            sx={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
            }}
        >
            <Box
                onClick={handleClick}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                sx={{
                    position: 'relative',
                    width: size,
                    height: size,
                    //borderRadius: rounded ? '50%' : 2,
                    overflow: 'hidden',

                    cursor: disabled ? 'default' : 'pointer',

                    backgroundColor: 'grey.100',

                    border: '2px dashed',
                    borderColor: value ? 'transparent' : 'grey.300',

                    transition: 'all 0.2s ease',

                    '&:hover': {
                        borderColor: disabled ? 'grey.300' : 'primary.main',
                        transform: disabled ? 'none' : 'scale(1.02)',
                    },
                }}
            >
                {/* Imagem */}
                {value ? (
                    <Box
                        component="img"
                        src={value}
                        alt="Imagem selecionada"
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            color: 'text.secondary',
                        }}
                    >
                        <PhotoCameraOutlinedIcon
                            sx={{
                                fontSize: size * 0.25,
                            }}
                        />

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 500,
                            }}
                        >
                            Adicionar imagem
                        </Typography>
                    </Box>
                )}

                {/* Overlay */}
                {value && isHovering && !disabled && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,

                            backgroundColor: 'rgba(0, 0, 0, 0.1)',

                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',

                            color: 'white',

                            transition: 'opacity 0.2s ease',
                        }}
                    >
                        <PhotoCameraOutlinedIcon
                            sx={{
                                fontSize: 32,
                                mb: 0.5,
                                color: 'rgb(173, 169, 169)',
                            }}
                        />

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: 'rgb(173, 169, 169)',
                            }}
                        >
                            Alterar imagem
                        </Typography>
                    </Box>
                )}

                {/* Botão remover */}
                {value && !disabled && (
                    <Tooltip title="Remover imagem">
                        <IconButton
                            size="small"
                            onClick={handleRemove}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,

                                width: 32,
                                height: 32,

                                backgroundColor: 'rgba(255,255,255,0.9)',

                                boxShadow: 2,

                                '&:hover': {
                                    backgroundColor: 'white',
                                },
                            }}
                        >
                            <DeleteOutlineIcon fontSize="small" sx={{ 
                                color: 'rgb(173, 169, 169)', 
                            }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Input escondido */}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleChange}
                disabled={disabled}
            />

            {/* Erro */}
            {error && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{
                        textAlign: 'center',
                        maxWidth: size,
                    }}
                >
                    {error}
                </Typography>
            )}
        </Box>
    );
}